import TabContext from "@mui/lab/TabContext";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAuth } from "src/hooks/use-auth";
import { getActivePlan } from "src/slices/auth/thunks";
import { selectCurrentTab } from "src/slices/system/selectors";
import { getPlans } from "src/slices/system/thunks";
import { DashboardNavbar } from "./dashboard-navbar";
import { DashboardSidebar } from "./dashboard-sidebar";
import { DashboardTopbar } from "./dashboard-topbar";
import UpgradeModal from "./upgradeModal";
import AIWordsUpgradeModal from "./upgradeModal/aiWords";

import { getTopBar } from "src/slices/system/thunks";
import { useDispatch } from "src/store";
import { EditorNavbar } from "./brief/rightSection/aiArticle/editorNavbar";
import { ArticlePresenceProvider } from "./articleEditor/ArticlePresenceContext";

interface DashboardLayoutProps {
  children?: ReactNode;
  showSidebar?: boolean;
  isArticleEditor?: boolean;
  title?: string;
  description?: string;
  id?: string;
  isHideArticle?: string;
  isOptimizedEditor?: boolean;
  isEEATMode?: boolean;
}

const DashboardLayoutRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
}));

export const DashboardLayout: FC<DashboardLayoutProps> = (props) => {
  const {
    children,
    showSidebar,
    title,
    id,
    description,
    isHideArticle,
    isOptimizedEditor,
    isEEATMode,
  } = props;
  const tab = useSelector(selectCurrentTab);
  const dispatch = useDispatch();
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isTopBarSeen, setIsTopBarSeen] = useState<boolean>(false);
  const [isTopBarAvailable, setIsTopBarAvailable] = useState<boolean>(false);
  const [topBarValues, setTopBarValues] = useState<any>({});
  const [daysLeft, setDaysLeft] = useState<number>(0);

  const [trialBarValues, setTrialBarValues] = useState<any>({
    message: `${daysLeft} days left in your free trial`,
    CTA: "Upgrade Now",
    // link: "https://app.aidashboard.ai/dashboard/pricing/",
    isActive: true,
    submit: null,
  });

  const [userStatus, setUserStatus] = useState<string>("");

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getPlans());
      dispatch(getActivePlan());
    }
  }, []);
  useEffect(() => {
    if (user?.isOnboardingShown === false || user?.isOnboardingShown) {
    } else if (
      isAuthenticated &&
      (!user?.plan?.next_bill_date || user?.plan?.status == "past_due") &&
      router.asPath !== "/dashboard/pricing/"
    ) {
      router.push({
        pathname: "/dashboard/pricing",
      });
    }

    setUserStatus(user?.plan?.status);
    //calculate days left with subscriptionStartsAt and nextBillDate

    if (user?.plan?.next_bill_date) {
      const nextBillDate = new Date(user?.plan?.next_bill_date);
      const currentDate = new Date();
      const diffTime = Math.abs(nextBillDate.getTime() - currentDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysLeft(diffDays);
      setTrialBarValues({
        ...trialBarValues,
        message: `${diffDays} days left in your free trial`,
      });
    }
  }, [router, user]);
  useEffect(() => {
    getTopBarValues().then((data) => {
      let topBarSeen: any = localStorage.getItem("topBarSeen");
      if (data) {
        if (data.isActive) {
          setIsTopBarAvailable(true);
          setTopBarValues(data);
        } else {
          setIsTopBarAvailable(false);
        }
      }
      if (topBarSeen) {
        topBarSeen = JSON.parse(topBarSeen);

        if (
          topBarSeen.isTopBarSeen === "true" &&
          topBarSeen.topBarId === data?.topBarId
        ) {
          setIsTopBarSeen(true);
        } else {
          setIsTopBarSeen(false);
        }
      } else {
        setIsTopBarSeen(false);
      }
    });
  }, []);

  const getTopBarValues = async () => {
    const getTopBarValues: any = await dispatch(getTopBar());
    if (getTopBarValues?.payload?.success) {
      return getTopBarValues?.payload?.data[0];
    } else {
      return null;
    }
  };

  const handleClose = () => {
    let saveParameters = {
      isTopBarSeen: "true",
      email: user.email,
      topBarSeenAt: new Date().toISOString(),
      topBarId: topBarValues.topBarId,
    };
    localStorage.setItem("topBarSeen", JSON.stringify(saveParameters));
    //also set animation transition for closing

    setIsTopBarSeen(true);
  };

  return (
    <>
      <TabContext value={tab}>
        {isTopBarAvailable &&
        !isTopBarSeen &&
        userStatus != "trialing" &&
        !props?.isArticleEditor ? (
          <DashboardTopbar
            onClose={(): void => handleClose()}
            topBarValues={topBarValues}
          />
        ) : userStatus == "trialing" && !props?.isArticleEditor ? (
          <DashboardTopbar
            // onClose={(): void => handleClose()}
            isUpgradePlan={true}
            topBarValues={trialBarValues}
          />
        ) : (
          ""
        )}
        <ArticlePresenceProvider
          articleId={id || ""}
          enabled={!!props.isArticleEditor && !isEEATMode}
        >
          {!props.isArticleEditor && <DashboardNavbar />}
          {props.isArticleEditor && (
            <EditorNavbar
              onOpenSidebar={() => {}}
              title={title}
              id={id}
              description={description}
              isHideArticle={isHideArticle}
              isOptimizedEditor={isOptimizedEditor}
              isEEATMode={isEEATMode}
            />
          )}
          <UpgradeModal />
          <AIWordsUpgradeModal />
          <DashboardLayoutRoot sx={{ paddingLeft: showSidebar ? "290px" : 0 }}>
            <Box
              sx={{
                display: "flex",
                flex: "1 1 auto",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {children}
            </Box>
          </DashboardLayoutRoot>
        </ArticlePresenceProvider>
        {showSidebar && (
          <DashboardSidebar
            onClose={(): void => setIsSidebarOpen(false)}
            open={isSidebarOpen}
          />
        )}
      </TabContext>
    </>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node,
};
